import { Link } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Clock, Rocket, Sparkles, AlertTriangle } from 'lucide-react';
import { format } from 'date-fns';

interface TrialFirstEntryModalProps {
  open: boolean;
  onClose: () => void;
  trialEndDate: string;
  userName?: string;
}

export function TrialFirstEntryModal({
  open,
  onClose,
  trialEndDate,
  userName,
}: TrialFirstEntryModalProps) {
  const formattedEndDate = format(new Date(trialEndDate), 'MMMM d, yyyy');

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <DialogContent 
        className="sm:max-w-lg"
      >
        <DialogHeader className="text-center">
          <div className="mx-auto mb-4 p-3 rounded-full bg-primary/10 w-fit">
            <Rocket className="w-8 h-8 text-primary" />
          </div>
          <DialogTitle className="text-2xl font-bold">
            Welcome{userName ? `, ${userName}` : ''}!
          </DialogTitle>
          <DialogDescription className="text-muted-foreground text-base">
            Your 14-day free trial has started.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Trial info card */}
          <div className="bg-muted/50 rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-primary" />
              <div>
                <p className="font-medium">Trial Duration</p>
                <p className="text-sm text-muted-foreground">
                  14 days – ends on {formattedEndDate}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              <div>
                <p className="font-medium">What happens at the end?</p>
                <p className="text-sm text-muted-foreground">
                  Your access will be paused until you choose a plan
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Sparkles className="w-5 h-5 text-green-500" />
              <div>
                <p className="font-medium">Upgrade anytime</p>
                <p className="text-sm text-muted-foreground">
                  Visit Settings → Billing to upgrade or manage your account
                </p>
              </div>
            </div>
          </div>

          {/* Important notice */}
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
            <p className="text-sm text-amber-700 dark:text-amber-400">
              <strong>Important:</strong> Make sure to upgrade before your trial ends to keep your data and continue using Decision Sales OS.
            </p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            className="flex-1 order-2 sm:order-1"
          >
            Start exploring
          </Button>
          <Link to="/pricing" className="flex-1 order-1 sm:order-2">
            <Button 
              variant="hero" 
              className="w-full gap-1.5"
              onClick={onClose}
            >
              <Sparkles className="w-4 h-4" />
              View plans
            </Button>
          </Link>
        </div>
      </DialogContent>
    </Dialog>
  );
}
